import React, { useState } from 'react';
import { Box, Typography, Alert, useTheme } from '@mui/material';
import EmojiEventsIcon from '@mui/icons-material/EmojiEvents';

export interface KnockoutMatch {
  id: string;
  team1: string;
  team2: string;
  group1?: string;
  group2?: string;
  winner?: string;
}

export interface KnockoutBracketData {
  rounds: KnockoutMatch[][];
  roundLabels: string[];
}

const ROUND_LABELS = ['16 Besar', 'Perempat Final', 'Semi Final', 'Final'];

const ROUND_ACCENT = ['#1976d2', '#FF9800', '#9C27B0', '#FFD700'];

export function generateKnockoutBracket(knockoutTeams: any[], tournamentMatches: any[]): KnockoutBracketData {
  // Get group winners and runners-up from Gugur sheet using Position column
  const groupResults: Record<string, { winner: string; runnerUp: string }> = {};
  
  // First, group teams by their group
  const teamsByGroup: Record<string, any[]> = {};
  knockoutTeams.forEach((team: any) => {
    const group = team.Grup;
    if (!teamsByGroup[group]) {
      teamsByGroup[group] = [];
    }
    teamsByGroup[group].push(team);
  });

  // Sort each group by position (1 = winner, 2 = runner-up)
  Object.keys(teamsByGroup).forEach(group => {
    teamsByGroup[group].sort((a, b) => parseInt(a.Position) - parseInt(b.Position));
    
    if (teamsByGroup[group].length >= 2) {
      groupResults[group] = {
        winner: teamsByGroup[group][0]['Nama Tim'],
        runnerUp: teamsByGroup[group][1]['Nama Tim']
      };
    }
  });

  console.log('Group results from Gugur sheet:', groupResults);

  const groups = Object.keys(groupResults).sort();
  const round16: KnockoutMatch[] = [];

  // Standard World Cup bracket structure:
  // Upper half: Winner A vs Runner-up B, Winner C vs Runner-up D, Winner E vs Runner-up F, Winner G vs Runner-up H
  // Lower half: Winner H vs Runner-up G, Winner F vs Runner-up E, Winner D vs Runner-up C, Winner B vs Runner-up A
  
  // Upper half matches
  for (let i = 0; i < groups.length; i += 2) {
    if (i + 1 < groups.length) {
      const groupA = groups[i];
      const groupB = groups[i + 1];
      
      if (groupResults[groupA] && groupResults[groupB]) {
        round16.push({
          id: `R16-U-${i/2}`,
          team1: groupResults[groupA].winner,
          team2: groupResults[groupB].runnerUp,
          group1: groupA,
          group2: groupB,
          winner: undefined,
        });
      }
    }
  }
  
  // Lower half matches (reverse order)
  for (let i = groups.length - 1; i >= 1; i -= 2) {
    const groupA = groups[i];
    const groupB = groups[i - 1];
    
    if (groupResults[groupA] && groupResults[groupB]) {
      round16.push({
        id: `R16-L-${(groups.length - 1 - i)/2}`,
        team1: groupResults[groupA].winner,
        team2: groupResults[groupB].runnerUp,
        group1: groupA,
        group2: groupB,
        winner: undefined,
      });
    }
  }

  console.log('Generated round16:', round16);

  const buildNextRound = (prevRound: KnockoutMatch[], prefix: string): KnockoutMatch[] => {
    const next: KnockoutMatch[] = [];
    for (let i = 0; i < prevRound.length; i += 2) {
      if (i + 1 < prevRound.length) {
        next.push({
          id: `${prefix}-${next.length + 1}`,
          team1: '',
          team2: '',
          winner: undefined,
        });
      }
    }
    return next;
  };

  const quarterFinals = buildNextRound(round16, 'QF');
  const semiFinals = buildNextRound(quarterFinals, 'SF');
  const finals = buildNextRound(semiFinals, 'FINAL');

  const rounds = [round16, quarterFinals, semiFinals, finals].filter((r) => r.length > 0);
  const roundLabels = ROUND_LABELS.slice(ROUND_LABELS.length - rounds.length);

  return { rounds, roundLabels };
}

interface KnockoutBracketProps {
  knockoutTeams: any[];
  tournamentMatches: any[];
}

const TEAM_BOX_HEIGHT = 52;
const MATCH_GAP = 12;
const MATCH_BLOCK_HEIGHT = TEAM_BOX_HEIGHT * 2 + 4;
const COL_WIDTH = 168;
const CONNECTOR_WIDTH = 36;
const CHAMPION_COL_WIDTH = 120;
const CHAMPION_BOX_HEIGHT = 108;
const HEADER_HEIGHT = 36;
const PADDING = 24;

function getMatchCenterY(roundIdx: number, matchIdx: number, firstRoundCount: number): number {
  if (roundIdx === 0) {
    return HEADER_HEIGHT + matchIdx * (MATCH_BLOCK_HEIGHT + MATCH_GAP) + MATCH_BLOCK_HEIGHT / 2;
  }
  const y1 = getMatchCenterY(roundIdx - 1, matchIdx * 2, firstRoundCount);
  const y2 = getMatchCenterY(roundIdx - 1, matchIdx * 2 + 1, firstRoundCount);
  return (y1 + y2) / 2;
}

function getMatchTop(roundIdx: number, matchIdx: number, firstRoundCount: number): number {
  return getMatchCenterY(roundIdx, matchIdx, firstRoundCount) - MATCH_BLOCK_HEIGHT / 2;
}

const KnockoutBracket: React.FC<KnockoutBracketProps> = ({ knockoutTeams, tournamentMatches }) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const [trophyImgError, setTrophyImgError] = useState(false);
  const { rounds, roundLabels } = generateKnockoutBracket(knockoutTeams, tournamentMatches);

  // Get knockout matches sorted by ID
  const knockoutMatches = tournamentMatches
    .sort((a: any, b: any) => parseInt(a.ID) - parseInt(b.ID));

  // Determine winner from match using score
  const getMatchWinner = (team1: string, team2: string): string | null => {
    const match = knockoutMatches.find((m: any) => 
      (m['Team A'] === team1 && m['Team B'] === team2) ||
      (m['Team A'] === team2 && m['Team B'] === team1)
    );
    if (match) {
      const scoreA = parseInt(match['Score A']);
      const scoreB = parseInt(match['Score B']);
      
      // Team with score 30 wins
      if (scoreA === 30 && scoreB !== 30) {
        return match['Team A'];
      }
      if (scoreB === 30 && scoreA !== 30) {
        return match['Team B'];
      }
    }
    return null;
  };

  // Get winners from specific round column (R16, R8, R4, R2)
  const getRoundWinners = (roundColumn: string): string[] => {
    const winners: string[] = [];
    knockoutMatches.forEach((match: any) => {
      const roundInfo = match[roundColumn];
      // If the round column has any value (e.g., "x", "1", etc.), this match belongs to that round
      if (roundInfo && roundInfo.trim() !== '') {
        const scoreA = parseInt(match['Score A']);
        const scoreB = parseInt(match['Score B']);
        
        if (scoreA === 30 && scoreB !== 30) {
          winners.push(match['Team A']);
        } else if (scoreB === 30 && scoreA !== 30) {
          winners.push(match['Team B']);
        }
      }
    });
    return winners;
  };

  // Fill in bracket with actual winners based on round columns
  const filledRounds: KnockoutMatch[][] = [];

  for (let roundIdx = 0; roundIdx < rounds.length; roundIdx++) {
    if (roundIdx === 0) {
      // Round 1 (16 Besar) - use teams from Gugur sheet, update with R16 winners
      const updatedRound = rounds[0].map((match) => {
        const winner = getMatchWinner(match.team1, match.team2);
        if (winner) {
          return { ...match, winner };
        }
        return match;
      });
      filledRounds.push(updatedRound);
    } else if (roundIdx === 1) {
      // Quarter Finals - auto-fill with R16 winners, or use R8 column if available
      const r8Winners = getRoundWinners('R8');
      const prevRound = filledRounds[0];
      const updatedRound = rounds[1].map((match, matchIdx) => {
        const manualWinner1 = r8Winners[matchIdx * 2];
        const manualWinner2 = r8Winners[matchIdx * 2 + 1];
        const source1 = prevRound[matchIdx * 2];
        const source2 = prevRound[matchIdx * 2 + 1];

        const newMatch = {
          ...match,
          team1: manualWinner1 || source1?.winner || '',
          team2: manualWinner2 || source2?.winner || '',
        };

        const winner = getMatchWinner(newMatch.team1, newMatch.team2);
        if (winner) newMatch.winner = winner;
        return newMatch;
      });
      filledRounds.push(updatedRound);
    } else if (roundIdx === 2) {
      // Semi Finals - auto-fill with QF winners, or use R4 column if available
      const r4Winners = getRoundWinners('R4');
      const prevRound = filledRounds[1];
      const updatedRound = rounds[2].map((match, matchIdx) => {
        const manualWinner1 = r4Winners[matchIdx * 2];
        const manualWinner2 = r4Winners[matchIdx * 2 + 1];
        const source1 = prevRound[matchIdx * 2];
        const source2 = prevRound[matchIdx * 2 + 1];

        const newMatch = {
          ...match,
          team1: manualWinner1 || source1?.winner || '',
          team2: manualWinner2 || source2?.winner || '',
        };

        const winner = getMatchWinner(newMatch.team1, newMatch.team2);
        if (winner) newMatch.winner = winner;
        return newMatch;
      });
      filledRounds.push(updatedRound);
    } else if (roundIdx === 3) {
      // Final - auto-fill with SF winners, or use R2 column if available
      const r2Winners = getRoundWinners('R2');
      const prevRound = filledRounds[2];
      const updatedRound = rounds[3].map((match, matchIdx) => {
        const manualWinner1 = r2Winners[matchIdx * 2];
        const manualWinner2 = r2Winners[matchIdx * 2 + 1];
        const source1 = prevRound[matchIdx * 2];
        const source2 = prevRound[matchIdx * 2 + 1];

        const newMatch = {
          ...match,
          team1: manualWinner1 || source1?.winner || '',
          team2: manualWinner2 || source2?.winner || '',
        };

        const winner = getMatchWinner(newMatch.team1, newMatch.team2);
        if (winner) newMatch.winner = winner;
        return newMatch;
      });
      filledRounds.push(updatedRound);
    } else {
      filledRounds.push(rounds[roundIdx]);
    }
  }

  if (rounds.length === 0 || rounds[0].length === 0) {
    return <Alert severity="info">Belum ada data babak gugur</Alert>;
  }

  const firstRoundCount = rounds[0].length;
  const finalRoundIdx = filledRounds.length - 1;
  const finalCenterY = getMatchCenterY(finalRoundIdx, 0, firstRoundCount);
  const finalColRight = PADDING + (finalRoundIdx + 1) * COL_WIDTH + finalRoundIdx * CONNECTOR_WIDTH;
  const championColLeft = finalColRight + CONNECTOR_WIDTH;

  const totalHeight =
    HEADER_HEIGHT +
    firstRoundCount * MATCH_BLOCK_HEIGHT +
    (firstRoundCount - 1) * MATCH_GAP +
    PADDING;
  const totalWidth =
    filledRounds.length * COL_WIDTH +
    (filledRounds.length - 1) * CONNECTOR_WIDTH +
    CONNECTOR_WIDTH +
    CHAMPION_COL_WIDTH +
    PADDING * 2;

  const getRoundStyles = (roundIdx: number) => {
    const accent = ROUND_ACCENT[roundIdx] ?? theme.palette.primary.main;
    return {
      accent,
      bg: isDark ? 'rgba(59,130,246,0.18)' : 'rgba(37,99,235,0.12)',
      border: isDark ? '#60a5fa' : '#2563eb',
      shadow: isDark ? '0 4px 12px rgba(96,165,250,0.22)' : '0 2px 8px rgba(37,99,235,0.18)',
      text: isDark ? '#eff6ff' : '#0f172a',
      subtext: isDark ? '#dbeafe' : '#1d4ed8',
      labelColor: ROUND_ACCENT[roundIdx] ?? accent,
    };
  };

  const connectorColor = isDark ? 'rgba(99,170,255,0.45)' : 'rgba(0,0,0,0.22)';

  const lines: React.ReactNode[] = [];
  for (let r = 0; r < filledRounds.length - 1; r++) {
    const colRight = PADDING + (r + 1) * COL_WIDTH + r * CONNECTOR_WIDTH;
    const colLeft = colRight + CONNECTOR_WIDTH;
    const midX = colRight + CONNECTOR_WIDTH / 2;

    for (let i = 0; i < filledRounds[r + 1].length; i++) {
      const yNext = getMatchCenterY(r + 1, i, firstRoundCount);
      const y1 = getMatchCenterY(r, i * 2, firstRoundCount);
      const y2 = getMatchCenterY(r, i * 2 + 1, firstRoundCount);

      lines.push(
        <path
          key={`conn-${r}-${i}-a`}
          d={`M ${colRight} ${y1} H ${midX} V ${yNext} H ${colLeft}`}
          stroke={connectorColor}
          strokeWidth={2}
          fill="none"
        />
      );
      lines.push(
        <path
          key={`conn-${r}-${i}-b`}
          d={`M ${colRight} ${y2} H ${midX} V ${yNext}`}
          stroke={connectorColor}
          strokeWidth={2}
          fill="none"
        />
      );
    }
  }

  lines.push(
    <path
      key="conn-champion"
      d={`M ${finalColRight} ${finalCenterY} H ${championColLeft}`}
      stroke={isDark ? 'rgba(255,215,0,0.55)' : 'rgba(255,152,0,0.5)'}
      strokeWidth={2}
      fill="none"
    />
  );

  const championTop = finalCenterY - CHAMPION_BOX_HEIGHT / 2;

  // Get champion from final match
  const finalMatch = filledRounds[filledRounds.length - 1]?.[0];
  const champion = finalMatch ? getMatchWinner(finalMatch.team1, finalMatch.team2) : null;

  return (
    <Box>
      <Typography variant="h6" fontWeight={700} align="center" gutterBottom>
        Bracket Babak Gugur
      </Typography>
      <Box
        sx={{
          overflowX: 'auto',
          overflowY: 'hidden',
          WebkitOverflowScrolling: 'touch',
          borderRadius: 2,
          bgcolor: isDark ? 'rgba(255,255,255,0.02)' : 'rgba(0,0,0,0.02)',
          p: { xs: 1, sm: 2 },
        }}
      >
        <Box
          sx={{
            position: 'relative',
            width: totalWidth,
            height: totalHeight,
            minWidth: '100%',
            mx: 'auto',
          }}
        >
          <svg
            width={totalWidth}
            height={totalHeight}
            style={{ position: 'absolute', inset: 0, pointerEvents: 'none', zIndex: 0 }}
          >
            {lines}
          </svg>

          {filledRounds.map((roundMatches, roundIdx) => {
            const styles = getRoundStyles(roundIdx);
            const colLeft = PADDING + roundIdx * (COL_WIDTH + CONNECTOR_WIDTH);

            return (
              <Box
                key={`round-${roundIdx}`}
                sx={{ position: 'absolute', left: colLeft, top: 0, width: COL_WIDTH, zIndex: 1 }}
              >
                <Box
                  sx={{
                    height: HEADER_HEIGHT,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    p: 0.5,
                    borderRadius: 1,
                    background: isDark ? 'transparent' : '#ffffff',
                    border: isDark ? 'none' : `2px solid ${styles.border}`,
                    boxShadow: isDark ? 'none' : styles.shadow,
                  }}
                >
                  <Typography
                    variant="subtitle2"
                    fontWeight={700}
                    sx={{
                      color: styles.labelColor,
                      fontSize: { xs: '0.75rem', sm: '0.875rem' },
                    }}
                  >
                    {roundLabels[roundIdx]}
                  </Typography>
                </Box>

                {roundMatches.map((match, matchIdx) => {
                  const top = getMatchTop(roundIdx, matchIdx, firstRoundCount);

                  return (
                    <Box
                      key={match.id}
                      sx={{
                        position: 'absolute',
                        top,
                        left: 0,
                        width: COL_WIDTH,
                        height: MATCH_BLOCK_HEIGHT,
                      }}
                    >
                      <Box
                        sx={{
                          p: 1,
                          borderRadius: 1,
                          background: (match as any).winner === match.team1
                            ? (isDark ? 'rgba(76,175,80,0.22)' : 'rgba(76,175,80,0.12)')
                            : styles.bg,
                          border: (match as any).winner === match.team1
                            ? '2px solid #4CAF50'
                            : `2px solid ${styles.border}`,
                          mb: '4px',
                          height: TEAM_BOX_HEIGHT,
                          boxShadow: (match as any).winner === match.team1
                            ? (isDark ? '0 4px 12px rgba(76,175,80,0.3)' : '0 2px 8px rgba(76,175,80,0.2)')
                            : styles.shadow,
                          display: 'flex',
                          flexDirection: 'column',
                          justifyContent: 'center',
                          alignItems: 'center',
                          textAlign: 'center',
                          overflow: 'hidden',
                        }}
                      >
                        <Typography
                          variant="body2"
                          fontWeight={700}
                          noWrap
                          title={match.team1 || 'Belum ada pemenang'}
                          sx={{
                            fontSize: { xs: '0.72rem', sm: '0.8rem' },
                            color: styles.text,
                            textAlign: 'center',
                            width: '100%',
                          }}
                        >
                          {match.team1 || ''}
                        </Typography>
                      </Box>
                      <Box
                        sx={{
                          p: 1,
                          borderRadius: 1,
                          background: (match as any).winner === match.team2
                            ? (isDark ? 'rgba(76,175,80,0.22)' : 'rgba(76,175,80,0.12)')
                            : styles.bg,
                          border: (match as any).winner === match.team2
                            ? '2px solid #4CAF50'
                            : `2px solid ${styles.border}`,
                          height: TEAM_BOX_HEIGHT,
                          boxShadow: (match as any).winner === match.team2
                            ? (isDark ? '0 4px 12px rgba(76,175,80,0.3)' : '0 2px 8px rgba(76,175,80,0.2)')
                            : styles.shadow,
                          display: 'flex',
                          flexDirection: 'column',
                          justifyContent: 'center',
                          alignItems: 'center',
                          textAlign: 'center',
                          overflow: 'hidden',
                        }}
                      >
                        <Typography
                          variant="body2"
                          fontWeight={700}
                          noWrap
                          title={match.team2 || 'Belum ada pemenang'}
                          sx={{
                            fontSize: { xs: '0.72rem', sm: '0.8rem' },
                            color: styles.text,
                            textAlign: 'center',
                            width: '100%',
                          }}
                        >
                          {match.team2 || ''}
                        </Typography>
                      </Box>
                    </Box>
                  );
                })}
              </Box>
            );
          })}

          {/* Champion */}
          <Box
            sx={{
              position: 'absolute',
              left: championColLeft,
              top: championTop,
              width: CHAMPION_COL_WIDTH,
              height: CHAMPION_BOX_HEIGHT,
              zIndex: 1,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 0.5,
              p: 1,
              borderRadius: 2,
              background: isDark
                ? 'linear-gradient(145deg, rgba(255,215,0,0.18) 0%, rgba(255,152,0,0.12) 100%)'
                : 'linear-gradient(145deg, rgba(255,248,225,1) 0%, rgba(255,236,179,0.9) 100%)',
              border: isDark ? '2px solid rgba(255,215,0,0.5)' : '2px solid rgba(255,193,7,0.6)',
              boxShadow: isDark
                ? '0 6px 20px rgba(255,215,0,0.25)'
                : '0 4px 16px rgba(255,193,7,0.35)',
            }}
          >
            {trophyImgError ? (
              <EmojiEventsIcon
                sx={{
                  fontSize: { xs: 40, sm: 48 },
                  color: '#FFD700',
                  filter: isDark ? 'drop-shadow(0 2px 6px rgba(255,215,0,0.4))' : 'none',
                }}
              />
            ) : (
              <Box
                component="img"
                src="/ranks/trophy-cup-svgrepo-com.svg"
                alt="Piala Champion"
                sx={{
                  width: { xs: 40, sm: 48 },
                  height: { xs: 40, sm: 48 },
                  objectFit: 'contain',
                  filter: isDark ? 'drop-shadow(0 2px 6px rgba(255,215,0,0.4))' : 'none',
                }}
                onError={() => setTrophyImgError(true)}
              />
            )}
            <Typography
              variant="subtitle2"
              fontWeight={800}
              sx={{
                color: isDark ? '#FFD700' : '#FF8F00',
                fontSize: { xs: '0.7rem', sm: '0.8rem' },
                letterSpacing: 0.5,
                textTransform: 'uppercase',
                textAlign: 'center',
                lineHeight: 1.2,
              }}
            >
              Champion
            </Typography>
            {champion && (
              <Typography
                variant="caption"
                fontWeight={600}
                sx={{
                  color: isDark ? '#FFD700' : '#FF8F00',
                  fontSize: { xs: '0.6rem', sm: '0.65rem' },
                  textAlign: 'center',
                  lineHeight: 1.2,
                  maxWidth: CHAMPION_COL_WIDTH - 16,
                  wordBreak: 'break-word',
                  px: 1,
                }}
                title={champion}
              >
                {champion}
              </Typography>
            )}
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default KnockoutBracket;
